import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { TOAST_TYPE } from '@base'
import { Input } from '@baseV2'
import { strings } from '@constants'
import { showToast } from '@data/state/action'

import BillingDetailsV2 from './BillingDetailsV2'
import { Card, SectionTitle, DateStack, BillingGrid, TermsTextarea } from './styles'

// epoch-ms → yyyy-mm-dd for a native date input (local).
const toDateInput = (value) => {
	if (!value) return ''
	const d = new Date(value)
	if (Number.isNaN(d.getTime())) return ''
	const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
	return local.toISOString().slice(0, 10)
}

// Page 2: Billing Details (address cards), then Delivery Dates | Terms & Conditions side by side.
const DetailsPage = ({ state, valueChangeHandler }) => {
	const { newSpo } = state
	const globalDispatch = useDispatch()

	const dispatchStartDate = newSpo?.dispatch_start_date
	const expectedDeliveryDate = newSpo?.expected_delivery_date

	// An SPO that arrives with only a delivery date starts dispatching on that same day, so seed the
	// empty Dispatch Start Date from it instead of leaving the field blank.
	useEffect(() => {
		if (expectedDeliveryDate && !dispatchStartDate) {
			valueChangeHandler('dispatch_start_date')(expectedDeliveryDate)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expectedDeliveryDate, dispatchStartDate])

	const onDateChange = (key) => (e) => {
		const v = e?.target?.value
		const time = v ? new Date(v).getTime() : null

		// Delivery can never happen before dispatch starts. The native picker is bounded by min/max,
		// but a typed date still reaches here, so reject it instead of storing an impossible pair.
		if (key === 'expected_delivery_date' && time && dispatchStartDate && time < dispatchStartDate) {
			globalDispatch(showToast(true, strings('msg_expected_delivery_before_dispatch_start'), { type: TOAST_TYPE.ERROR }))
			return
		}
		if (key === 'dispatch_start_date' && time && expectedDeliveryDate && time > expectedDeliveryDate) {
			globalDispatch(showToast(true, strings('msg_expected_delivery_before_dispatch_start'), { type: TOAST_TYPE.ERROR }))
			return
		}

		valueChangeHandler(key)(time)
	}

	return (
		<>
			<BillingDetailsV2 state={state} valueChangeHandler={valueChangeHandler} />

			<BillingGrid>
				<Card>
					<SectionTitle>{strings('delivery_dates')}</SectionTitle>
					<DateStack>
						<Input
							type='date'
							label={strings('dispatch_start_date')}
							value={toDateInput(dispatchStartDate)}
							max={toDateInput(expectedDeliveryDate) || undefined}
							onChange={onDateChange('dispatch_start_date')}
						/>
						<Input
							type='date'
							label={strings('expected_delivery_date')}
							value={toDateInput(expectedDeliveryDate)}
							min={toDateInput(dispatchStartDate) || undefined}
							onChange={onDateChange('expected_delivery_date')}
						/>
					</DateStack>
				</Card>

				<Card>
					<SectionTitle>Terms and Conditions</SectionTitle>
					<TermsTextarea
						id='spo-terms'
						rows={8}
						placeholder={strings('msg_share_any_more_details')}
						value={newSpo?.terms ?? ''}
						onChange={(e) => valueChangeHandler('terms')(e?.target ? e.target.value : e)}
					/>
				</Card>
			</BillingGrid>
		</>
	)
}

export default DetailsPage
